/// <reference types="@types/segment-analytics" />

import {AnalyticsEvent} from '#/analytics/analytics-events';
import {updateBrazeUserFromUserProfile} from '#/braze/update-braze-user-from-user-profile';
import {TransactionData} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {transactionTypeConstants} from '#/components/transaction/transaction-type-constants';
import environment from '#/environment/environment';
import {decimalsCount, decimalsMultiplier} from '#/utils/contract-consts';
import * as braze from '@braze/web-sdk';
import {Land, LandAccountInfo, LandMarketInfo, TransactionType, UserProfilePrivate} from 'shareland-common';

export const analyticsHandler = {
  track: (event: AnalyticsEvent[keyof AnalyticsEvent]) => {
    const {event: eventName, ...params} = event;

    if (window.analytics) {
      window.analytics.track(eventName, params);
    }

    if (environment.brazeApiKey) {
      braze.logCustomEvent(eventName, params);
    }
  },
  identify: (params: UserProfilePrivate & {method: string}) => {
    if (window.analytics) {
      window.analytics.identify(params.account.toLowerCase(), {method: params.method});
    }

    if (environment.brazeApiKey) {
      braze.changeUser(params.brazeId);
      updateBrazeUserFromUserProfile(params);
    }
  },
  page: (params: Record<string, unknown>) => {
    if (!window.analytics) return;
    window.analytics.page(params);
  },
};

export const sendAnalyticsEvent = {
  login: analyticsHandler.identify,
  fundingStarted: (params: Omit<AnalyticsEvent['fundingStarted'], 'event'>) => {
    const eventValue: AnalyticsEvent['fundingStarted'] = {
      event: 'funding_started',
      ...params,
    };
    analyticsHandler.track(eventValue);
  },
  fundingCompleted: (params: Omit<AnalyticsEvent['fundingCompleted'], 'event'>) => {
    const eventValue: AnalyticsEvent['fundingCompleted'] = {
      event: 'funding_completed',
      ...params,
    };
    analyticsHandler.track(eventValue);
  },
  search: (params: Omit<AnalyticsEvent['search'], 'event'>) => {
    const eventValue: AnalyticsEvent['search'] = {
      event: 'search',
      ...params,
    };
    analyticsHandler.track(eventValue);
  },
  exploreDataOrientation: (params: Omit<AnalyticsEvent['exploreDataOrientation'], 'event'>) => {
    const eventValue: AnalyticsEvent['exploreDataOrientation'] = {
      event: 'explore_data_orientation',
      ...params,
    };
    analyticsHandler.track(eventValue);
  },
  viewProperty: (params: {land: Land; landMarketInfo: LandMarketInfo | undefined}) => {
    const {land, landMarketInfo} = params;

    const eventValue: AnalyticsEvent['viewProperty'] = {
      event: 'view_property',
      ...getLand(land),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
    };

    analyticsHandler.track(eventValue);
  },
  mapViewProperty: (params: {land: Land}) => {
    const {land} = params;

    const eventValue: AnalyticsEvent['mapViewProperty'] = {
      event: 'map_view_property',
      ...getLand(land),
      market_variation: getMarketVariationValue(land),
    };

    analyticsHandler.track(eventValue);
  },
  advancedCharts: () => {
    const eventValue: AnalyticsEvent['advancedCharts'] = {
      event: 'advanced_charts',
    };
    analyticsHandler.track(eventValue);
  },
  favoriteProperty: (params: {land: Land; landMarketInfo: LandMarketInfo | undefined; value: boolean}) => {
    const {land, landMarketInfo, value} = params;

    const eventValue: AnalyticsEvent['favoriteProperty'] = {
      event: 'favorite_property',
      ...getLand(land),
      real_world_price: getOraclePriceValue(landMarketInfo),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      value,
    };

    analyticsHandler.track(eventValue);
  },
  propertyComment: (params: {land: Land; landMarketInfo: LandMarketInfo; linkShared: boolean}) => {
    const {land, landMarketInfo, linkShared} = params;

    const eventValue: AnalyticsEvent['propertyComment'] = {
      event: 'property_comment',
      ...getLand(land),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      link_shared: linkShared,
    };
    analyticsHandler.track(eventValue);
  },
  propertyCommentInteraction: (params: {
    land: Land;
    landMarketInfo: LandMarketInfo;
    linkShared: boolean;
    interactionType: AnalyticsEvent['propertyCommentInteraction']['interaction_type'];
  }) => {
    const {land, landMarketInfo, linkShared, interactionType} = params;

    const eventValue: AnalyticsEvent['propertyCommentInteraction'] = {
      event: 'property_comment_interaction',
      ...getLand(land),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      link_shared: linkShared,
      interaction_type: interactionType,
    };
    analyticsHandler.track(eventValue);
  },
  userPortfolioConsultation: (params: Omit<AnalyticsEvent['userPortfolioConsultation'], 'event'>) => {
    const eventValue: AnalyticsEvent['userPortfolioConsultation'] = {
      event: 'user_portfolio_consultation',
      ...params,
    };
    analyticsHandler.track(eventValue);
  },
  previousTransaction: (params: Omit<AnalyticsEvent['previousTransaction'], 'event'>) => {
    const eventValue: AnalyticsEvent['previousTransaction'] = {
      event: 'previous_transaction',
      ...params,
    };
    analyticsHandler.track(eventValue);
  },
  propertyAsset: (params: {
    land: Land;
    landMarketInfo: LandMarketInfo;
    landAccountInfo: LandAccountInfo;
    premiumRatio: string;
  }) => {
    const {land, landMarketInfo, landAccountInfo} = params;

    const eventValue: AnalyticsEvent['propertyAsset'] = {
      event: 'property_asset',
      ...getLand(land),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      land_token_amount: tokenAmountToString(landAccountInfo.landTokenAmount),
      stable_token_amount: tokenAmountToString(landAccountInfo.landTokenMarketValue, 2),
      collateral_ratio: `${landAccountInfo.collateralRatio / 100}%`,
      premium_ratio: params.premiumRatio,
    };
    analyticsHandler.track(eventValue);
  },
  transactionStarted: (params: {
    land: Land;
    landMarketInfo: LandMarketInfo | undefined;
    transactionData: TransactionData;
  }) => {
    const {land, landMarketInfo} = params;

    const eventValue: AnalyticsEvent['transactionStarted'] = {
      event: 'transaction_started',
      ...getLand(land),
      real_world_price: getOraclePriceValue(landMarketInfo),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      ...getTransaction(params.transactionData),
    };

    analyticsHandler.track(eventValue);
  },
  transactionCompleted: (params: {
    land: Land;
    landMarketInfo: LandMarketInfo | undefined;
    transactionData: TransactionData;
  }) => {
    const {land, landMarketInfo} = params;

    const eventValue: AnalyticsEvent['transactionCompleted'] = {
      event: 'transaction_completed',
      ...getLand(land),
      real_world_price: getOraclePriceValue(landMarketInfo),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      ...getTransaction(params.transactionData),
    };

    analyticsHandler.track(eventValue);
  },
  transactionFailed: (params: {
    land: Land;
    landMarketInfo: LandMarketInfo | undefined;
    transactionData: TransactionData;
    cause: string;
  }) => {
    const {land, landMarketInfo} = params;

    const eventValue: AnalyticsEvent['transactionFailed'] = {
      event: 'transaction_failed',
      ...getLand(land),
      real_world_price: getOraclePriceValue(landMarketInfo),
      market_price: getMarketPriceValue(landMarketInfo),
      market_variation: getMarketVariationValue(land),
      ...getTransaction(params.transactionData),
      cause: params.cause,
    };

    analyticsHandler.track(eventValue);
  },
  editUserProfile: () => {
    const eventValue: AnalyticsEvent['editUserProfile'] = {
      event: 'edit_user_profile',
    };
    analyticsHandler.track(eventValue);
  },
  feedbackSubmitted: () => {
    const eventValue: AnalyticsEvent['feedbackSubmitted'] = {
      event: 'feedback_submitted',
    };
    analyticsHandler.track(eventValue);
  },
  logout: () => {
    const eventValue: AnalyticsEvent['logout'] = {
      event: 'logout',
    };
    analyticsHandler.track(eventValue);
  },
  openAddFunds: (params: Omit<AnalyticsEvent['openAddFunds'], 'event'>) => {
    const eventValue: AnalyticsEvent['openAddFunds'] = {
      ...params,
      event: 'open_add_funds',
    };
    analyticsHandler.track(eventValue);
  },
  completeUserProfile: () => {
    const eventValue: AnalyticsEvent['completeUserProfile'] = {
      event: 'complete_user_profile',
    };

    analyticsHandler.track(eventValue);
  },
  addFunds: (params: Omit<AnalyticsEvent['addFunds'], 'event'>) => {
    const eventValue: AnalyticsEvent['addFunds'] = {
      ...params,
      event: 'add_funds',
    };

    analyticsHandler.track(eventValue);
  },
} satisfies Record<keyof AnalyticsEvent, Function>;

const getLand = (land: Land) => ({
  name: land.name,
  city: land.city,
  state: land.state,
});

const tokenAmountToString = (numberToFormat: number | undefined, decimals: number = decimalsCount): string => {
  if (typeof numberToFormat === 'number') {
    return numberToFormat !== 0
      ? Intl.NumberFormat('en-US', {maximumFractionDigits: decimals, minimumFractionDigits: decimals}).format(
          numberToFormat / decimalsMultiplier,
        )
      : '0';
  }
  return '';
};

const getOraclePriceValue = (landMarketInfo: LandMarketInfo | undefined) =>
  landMarketInfo ? tokenAmountToString(landMarketInfo.oraclePrice, 2) : '';
const getMarketPriceValue = (landMarketInfo: LandMarketInfo | undefined) =>
  landMarketInfo ? tokenAmountToString(landMarketInfo.marketPrice, 2) : '';
const getMarketVariationValue = (land: Land) =>
  (land.syntheticMarketData.marketPrice.daily.ratio * 100 ?? 0).toFixed(1);

const getTransaction = (transactionData: TransactionData) => ({
  transaction_type: transactionTypeConstants[transactionData.type].i18nKey,
  ...(() => {
    switch (transactionData.type) {
      case TransactionType.Purchase:
        return {
          transaction_data_land_token_amount: tokenAmountToString(transactionData.data.landTokenAmount),
          transaction_data_stable_token_amount: tokenAmountToString(transactionData.data.stableTokenAmount, 2),
          transaction_data_transaction_fee: tokenAmountToString(transactionData.data.transactionFee, 2),
        };
      case TransactionType.Sell:
        return {
          transaction_data_land_token_amount: tokenAmountToString(transactionData.data.landTokenAmount),
          transaction_data_stable_token_amount: tokenAmountToString(transactionData.data.stableTokenAmount, 2),
          transaction_data_transaction_fee: tokenAmountToString(transactionData.data.transactionFee, 2),
        };
      case TransactionType.OpenCollateralPosition:
        return {
          transaction_data_land_token_amount: tokenAmountToString(transactionData.data.landTokenAmount),
          transaction_data_stable_token_amount: tokenAmountToString(transactionData.data.stableTokenAmount, 2),
          transaction_data_transaction_fee: tokenAmountToString(transactionData.data.transactionFee, 2),
          transaction_data_collateral_ratio: transactionData.data.collateralRatio,
        };
      case TransactionType.CloseCollateralPosition:
        return {
          transaction_data_borrowed_land_token_amount: tokenAmountToString(
            transactionData.data.borrowedLandTokenAmount,
          ),
          transaction_data_stable_token_in_collateral_amount: tokenAmountToString(
            transactionData.data.stableTokenInCollateralAmount,
            2,
          ),
        };
      case TransactionType.AdjustCollateralPosition:
        return {
          transaction_data_land_token_amount: tokenAmountToString(transactionData.data.landTokenAmount),
          transaction_data_stable_token_amount: tokenAmountToString(transactionData.data.stableTokenAmount, 2),
          transaction_data_transaction_fee: tokenAmountToString(transactionData.data.transactionFee, 2),
          transaction_data_collateral_ratio: transactionData.data.collateralRatio,
          transaction_data_stable_amount_position_difference: tokenAmountToString(
            transactionData.data.stableAmountPositionDifference,
            2,
          ),
        };
      case TransactionType.MintLiquidityToken:
        return {
          transaction_data_land_token_amount: tokenAmountToString(transactionData.data.landTokenAmount),
          transaction_data_stable_token_amount: tokenAmountToString(transactionData.data.stableTokenAmount, 2),
          transaction_data_transaction_fee: tokenAmountToString(transactionData.data.transactionFee, 2),
        };
      case TransactionType.BurnLiquidityToken:
        return {
          transaction_data_land_token_amount: tokenAmountToString(transactionData.data.landTokenAmount),
          transaction_data_stable_token_amount: tokenAmountToString(transactionData.data.stableTokenAmount, 2),
        };
      case TransactionType.ClaimRewards:
        return {
          transaction_data_claimable_stable_amount: tokenAmountToString(transactionData.data.claimableStableAmount, 2),
        };
      default:
        return {};
    }
  })(),
});
